import { useAuth0 } from '@auth0/auth0-react';
import { useContext, useEffect, useRef, useState } from 'react';
import { store } from 'components/Store';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import useCallCollectContent from 'components/Dashboard/hooks/useCallCollectContent';
import useNumberOfUnseenItems from 'components/Dashboard/hooks/useNumberOfUnseenItems';
import masterTopicObject from 'components/Dashboard/MainSearchBar/masterTopicObject';
import getSearchWithinHash from 'components/Dashboard/utilities/getSearchWithinHash';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import normalize from 'components/Dashboard/utilities/normalizeString';
import {
  searchIncludesTitleConditional,
  relevantActivityFalseCondition,
} from 'components/Dashboard/StakeHolders/searchIncludesTitle';
import Stakeholder from './ui/Stakeholder';
import useFilterFromType from './useFilterFromType';
import useSendEmailToContacts from 'components/Dashboard/Influence/CrmContacts/CrmContactsTable/CrmContactsListBar/sendEmailToContacts';
import useGetContactsTags from 'components/Dashboard/Filters/useGetContactsTags';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import callHightlightContentAPI from 'components/Dashboard/utilities/callHightlightContentAPI';
import useGetHighlightingKeywords from 'components/Dashboard/hooks/useGetHighlighingKeywords';
import ChangeKeywordsExclusions from 'components/Dashboard/utilities/changeKeywordExclusions';
import { useGroupFunctions } from 'components/Dashboard/Influence/CrmGroups/GroupFunctions';
import useContactsFunctionForStakeholders from './useContactsFunctionForStakeholder';
import parliamentarianUsers from 'components/Dashboard/SidebarElements/parliamentarianUsers';
import getAppSite from 'utils/getAppSite';
import useEmailDomainsInitialCall from 'components/Dashboard/Influence/useEmailDomainsInitialCall';
import useAddMetaTags from 'components/Dashboard/utilities/addMetaTags';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';
import useStakeholderContentRelated from 'components/Dashboard/StakeHolders/StakeholderPage/useStakeholderContentRelated';

const StakeholderPage = () => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { contentResults, setContentResults } = useContentResults();
  const { activePlan, search, keywordsLists, team } = state;
  const { list, activeSearch } = search;
  const { enableStakeholders, code } = activePlan;

  const { isAuthenticated } = useAuth0();
  const source = new AbortController();

  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { id, contentType } = params;
  const { changeContentypeFromUrl } = useFilterFromType({ stakeholderPage: true });
  const { stakeholderSearchCondition, relatedContentCall, contentTypeFilters, callRelatedContent } =
    useStakeholderContentRelated();

  const { Stakeholders } = contentResults ?? {};
  const { contentRelated } = Stakeholders ? Stakeholders : {};

  const [isBioLoading, setIsBioLoading] = useState(true);
  const [isInitialActivityLoading, setIsInitialActivityLoading] = useState(true);
  const [isLoadingActivity, setIsLoadingActivity] = useState(false);
  const [clickOnSeeMore, setClickOnSeeMore] = useState(false);
  const { callCollectContent } = useCallCollectContent();
  const { addMetaTags } = useAddMetaTags();

  useEmailDomainsInitialCall();
  useGetContactsTags();

  const { andKeywords, queryHighlight } = useGetHighlightingKeywords();

  const createLocalTopic = () => {
    if (getUrlParam('topic-id')) {
      let keywordListid = getUrlParam('topic-id') === 'all' ? null : parseInt(getUrlParam('topic-id'));
      let listFromParam = keywordsLists.find((item) => item.id === keywordListid);
      return listFromParam ? listFromParam : null;
    } else {
      return list;
    }
  };
  const [localTopic, setLocalTopic] = useState(createLocalTopic());
  const [searchPageQuery, setSearchPageQuery] = useState(
    getUrlParam('search') && stakeholderSearchCondition ? getUrlParam('search') : ''
  );

  const loadNew = useRef(null);
  const dispatchInitialCall = useRef(null);
  const reloadOnSearch = useRef(null);
  const useFilterFromUrl = useRef(true);
  const isLoadNewRunning = useRef(false);
  const changeTopicOrQueryAbortController = useRef(null);
  const { generalApiCall } = useGeneralApiCall();
  const { numberOfUnseenItems } = useNumberOfUnseenItems();
  const { verifyIfContactIsAdded } = useContactsFunctionForStakeholders();
  const { listingGroupsForStakeholderOrContact } = useGroupFunctions();
  const { newAgencyPlan } = useTopicOrClientWord();

  const activityContainer = useRef(null);

  const searchParam = getUrlParam('search');
  const topicParam = getUrlParam('topic-id');

  const latestRequestId = useRef(null);

  useEffect(() => {
    const source = new AbortController();
    // Use the current id as the request id
    latestRequestId.current = id;
    const thisRequestId = id;
    if ((!topicParam || topicParam === 'all') && !searchParam && isAuthenticated) {
      const masterTopic = masterTopicObject(activePlan, keywordsLists);
      setLocalTopic(masterTopic);
      navigate({ search: `?topic-id=all` }, { replace: true });
    }
    dispatchInitialCall.current(source, thisRequestId);
    return () => {
      source.abort('Stakeholder item canceled by the user.');
    };
  }, [id, contentType, team]);

  dispatchInitialCall.current = (source, thisRequestId) => {
    try {
      if (location.pathname.includes('/monitor')) {
        let newUrl = location.pathname.replace('/monitor', '/influence');
        return navigate(newUrl);
      }
      let filters = useFilterFromUrl.current && getSearchWithinHash() ? getSearchWithinHash() : searchPageQuery;
      setSearchPageQuery(filters);
      loadNew.current(source, thisRequestId);
    } catch (e) {}
  };

  useEffect(() => {
    reloadOnSearch.current();
  }, [searchParam, topicParam]);

  reloadOnSearch.current = () => {
    // Don't run if loadNew is still running - it will handle the param change
    if (isLoadNewRunning.current) {
      return;
    }
    if (!!Stakeholders && Stakeholders.contact) {
      if (!!searchParam && searchParam !== searchPageQuery) {
        changeTopicOrQuery({ freeSearch: getUrlParam('search') });
      }
      if (topicParam && (localTopic === null || topicParam !== localTopic.id)) {
        changeTopicOrQuery({
          topic: keywordsLists.find((item) => item.id === (topicParam === 'all' ? null : parseInt(topicParam))),
        });
      }
      if (!topicParam && !searchParam && keywordsLists?.length === 0) {
        changeTopicOrQuery({ allActivity: true });
        seeAllActivity();
      }
      //OP: no topic or search param, so we need to see all activity
      if (!topicParam && !searchParam && keywordsLists?.length !== 0) {
        changeTopicOrQuery({ allActivity: true });
        seeAllActivity();
      }
    }
  };

  loadNew.current = async (source, thisRequestId) => {
    try {
      isLoadNewRunning.current = true;
      setIsBioLoading(true);
      setIsInitialActivityLoading(true);
      // Capture initial search params and topic to detect changes during execution
      const initialSearchParam = getUrlParam('search') ?? null;
      const initialTopicParam = getUrlParam('topic-id') ?? null;
      const initialLocalTopicId = localTopic?.id ?? null;
      const initialLocationSearch = location.search;

      // Helper function to check if params/topic changed
      const hasParamsOrTopicChanged = () => {
        const currentSearchParam = getUrlParam('search') ?? null;
        const currentTopicParam = getUrlParam('topic-id') ?? null;
        const currentLocalTopicId = localTopic?.id ?? null;
        const currentLocationSearch = location.search;

        const hasSearchParamChanged = initialSearchParam !== currentSearchParam;
        const hasTopicParamChanged = initialTopicParam !== currentTopicParam;
        const hasLocalTopicChanged = initialLocalTopicId !== currentLocalTopicId;
        const hasLocationChanged = initialLocationSearch !== currentLocationSearch;

        return hasSearchParamChanged || hasTopicParamChanged || hasLocalTopicChanged || hasLocationChanged;
      };

      // Helper function to check if aborted
      const isAborted = () => {
        return source?.signal?.aborted || false;
      };

      // Helper function to abort if params changed
      const abortIfParamsChanged = () => {
        if (hasParamsOrTopicChanged()) {
          source.abort('Params or topic changed during execution');
          // Don't set loading to false here - new call will start, loading should persist
          return true;
        }
        return false;
      };

      const pathname = `/api/stakeholder/${id}`;
      const method = 'get';
      const content = await generalApiCall({
        pathname,
        method,
        needsAuthentication: isAuthenticated,
        requestSource: source,
        notShowErrorMessage: true,
      });

      // Check if aborted or params changed after first API call
      if (isAborted() || abortIfParamsChanged()) {
        return;
      }

      if (content) {
        if (contentType && (!isAuthenticated || (isAuthenticated && !enableStakeholders))) {
          return navigate({ pathname: content?.canonicalUrl, search: location?.search }, { replace: true });
        }
        const isStaff = content?.employers?.length > 0;
        const contactInformation = isAuthenticated ? await verifyIfContactIsAdded({ contactId: content?.id }) : [];

        // Check if aborted or params changed after contactInformation call
        if (isAborted() || abortIfParamsChanged()) {
          return;
        }

        const groups =
          isAuthenticated && !parliamentarianUsers(code)
            ? await listingGroupsForStakeholderOrContact({ stakeholderContactId: content?.id, source })
            : [];

        // Check if aborted or params changed after groups call
        if (isAborted() || abortIfParamsChanged()) {
          return;
        }

        setContentResults((prev) => ({
          ...prev,
          Stakeholders: {
            ...(prev?.Stakeholders ?? {}),
            ...Stakeholders,
            contact: {
              ...content,
              isStaff,
              contactInformation,
              groups,
            },
            currentId: parseInt(id),
            contentRelated: {},
          },
        }));
        setIsBioLoading(false);

        let profileContentHightlighted;
        let matchInformation;
        let ranking;
        let relatedContent;
        let employers;

        if (isStaff) {
          employers = await callEmployersForStaff({ employers: content?.employers, source });

          // Check if aborted or params changed after employers call
          if (isAborted() || abortIfParamsChanged()) {
            return;
          }
        } else {
          //AE: This is to avoid continue using topic from previous client and got errors on query-by-keyword-list-api
          // Use current topic from URL, not stale state
          const topicToUse = createLocalTopic();

          // Check if params changed before making API calls
          if (isAborted() || abortIfParamsChanged()) {
            return;
          }

          // Compute matchInformation early so the positions section can render
          // before the first streamed activity section arrives.
          if (!!topicToUse?.id && topicToUse?.id !== null && isAuthenticated) {
            ranking = await callAgregatedSentiment({
              stakeholderId: content?.id,
              keywordListId: topicToUse?.id,
              source,
            });

            // Check if aborted or params changed after ranking call
            if (isAborted() || abortIfParamsChanged()) {
              return;
            }

            matchInformation = transformMatchingInformationCall({ ranking, source });

            setContentResults((prev) => ({
              ...prev,
              Stakeholders: {
                ...(prev?.Stakeholders ?? {}),
                ...Stakeholders,
                contact: {
                  ...content,
                  isStaff,
                  matchInformation: matchInformation ?? {},
                  ranking,
                  profile: content.profile,
                  contactInformation,
                  groups,
                },
                currentId: parseInt(id),
                contentRelated: {},
              },
            }));
          }

          const initialSearchParamIsNonEmpty = typeof initialSearchParam === 'string' && initialSearchParam !== '';
          const activeSearchResultsConditionalForRequest =
            ((initialLocalTopicId !== null && initialLocalTopicId !== undefined) || initialSearchParamIsNonEmpty) &&
            stakeholderSearchCondition;
          relatedContent = await relatedContentCall({
            content,
            freeSearch: queryToSearch({ officalName: content.officalName }),
            topic: topicToUse,
            sourceProp: source,
            activeSearchResultsConditional: activeSearchResultsConditionalForRequest,
          });

          // Check if aborted or params changed after relatedContent call
          if (isAborted() || abortIfParamsChanged()) {
            return;
          }

          profileContentHightlighted = await callHighlightApi({ content, source });

          // Check if aborted or params changed after highlight call
          if (isAborted() || abortIfParamsChanged()) {
            return;
          }

          // Use current topic from URL, not stale state.
          // (matchInformation is already computed above for initial loading)
          const currentTopic = createLocalTopic();
          if (!matchInformation && !!currentTopic?.id && currentTopic?.id !== null && isAuthenticated) {
            ranking = await callAgregatedSentiment({
              stakeholderId: content?.id,
              keywordListId: currentTopic?.id,
              source,
            });

            // Check if aborted or params changed after ranking call
            if (isAborted() || abortIfParamsChanged()) {
              return;
            }

            matchInformation = transformMatchingInformationCall({ ranking, source });
          }
        }
        if (String(thisRequestId) !== String(latestRequestId.current)) return;

        // Final check before updating state and calling addMetaData
        if (isAborted() || abortIfParamsChanged()) {
          return;
        }

        setContentResults({
          ...contentResults,
          Stakeholders: {
            ...Stakeholders,
            contact: {
              ...content,
              isStaff,
              matchInformation: matchInformation ?? {},
              contactInformation,
              ranking,
              profile:
                !searchIncludesTitleConditional(content.officalName) && profileContentHightlighted?.highlightedHtmlBody
                  ? profileContentHightlighted?.highlightedHtmlBody
                  : content.profile,
              groups,
              employers,
            },
            currentId: parseInt(id),
            contentRelated: relatedContent,
          },
        });
        useFilterFromUrl.current = false;
        setIsInitialActivityLoading(false);

        // One more check right before addMetaData to prevent navigation to previous topic
        if (isAborted() || abortIfParamsChanged()) {
          return;
        }

        addMetaData({ content });

        const listToSend =
          list !== null && list.id !== null && !getUrlParam('search') && stakeholderSearchCondition ? list : null;
        collectCall({ contact: content, topic: listToSend });
        await numberOfUnseenItems();
      }
    } catch (error) {
      if (String(thisRequestId) === String(latestRequestId.current)) {
        setIsBioLoading(false);
        setIsInitialActivityLoading(false);
      }
    } finally {
      isLoadNewRunning.current = false;
    }
  };

  const queryToSearch = ({ search, officalName }) => {
    const queryToCompare = search ?? getUrlParam('search');
    const queryToSearch =
      queryToCompare &&
      stakeholderSearchCondition &&
      !searchIncludesTitleConditional(officalName ?? Stakeholders?.contact?.officalName) &&
      !relevantActivityFalseCondition()
        ? queryToCompare
        : null;
    return queryToSearch;
  };

  const transformMatchingInformationCall = ({ ranking }) => {
    const {
      keywordMatchedPositions,
      keywordMatchedLordSelectCommittees,
      keywordMatchedAppgs,
      keywordMatchedSelectCommittees,
    } = ranking ?? {};
    const positions = keywordMatchedPositions ?? [];
    let appgs = keywordMatchedAppgs
      ? keywordMatchedAppgs?.map((item) => {
          return { name: item, type: 'All Party Groups' };
        })
      : [];
    let lordSelectCommittees = keywordMatchedLordSelectCommittees
      ? keywordMatchedLordSelectCommittees.map((item) => {
          return { name: item, type: 'Lords Select Committees' };
        })
      : [];
    let selectCommittees = keywordMatchedSelectCommittees
      ? keywordMatchedSelectCommittees?.map((item) => {
          return { name: item, type: 'Select Committees' };
        })
      : [];
    const groupMemberships = [...appgs, ...lordSelectCommittees, ...selectCommittees];
    return { positions, groupMemberships };
  };

  const addMetaData = (props) => {
    const { content } = props;
    const { name, descriptionMeta, categories, officalName } = content;
    let hashToShow = '';
    if (!enableStakeholders || !isAuthenticated) {
      let getViewParam = () => {
        const viewParam = getUrlParam('view');
        return viewParam && !location.search.includes('view=')
          ? `${location.search && location.search !== '' ? '&' : '?'}view=${viewParam}`
          : '';
      };
      const params = new URLSearchParams(location.search + getViewParam());
      params.delete('topic-id');
      params.delete('search');
      navigate(
        {
          pathname: location.pathname,
          search: params.toString(),
        },
        { replace: true }
      );
    }
    let documentTitle = `${
      contentType !== undefined ? `${changeContentypeFromUrl(contentType)} - ` : ''
    }${officalName}${categories.includes('Special Adviser') ? ', Special Adviser' : ''} - PolicyMogul`;
    let title = documentTitle;
    let hash = hashToShow;
    let description = descriptionMeta;
    let contentUrl = `/stakeholders/${id}/${normalize(name)}`;
    if (contentType === 'parliamentary-record') {
      navigate(`${contentUrl}/spoken-parliamentary-contributions`);
    }
    addMetaTags({ title, hash, contentUrl, description });
  };

  const callAgregatedSentiment = async ({ stakeholderId, keywordListId, source }) => {
    let pathname = `/api/stakeholder/ranking?stakeholderId=${stakeholderId}&keywordListId=${keywordListId}&country=${
      getAppSite() === 'usa' ? 'USA' : 'uk'
    }`;
    let method = 'get';
    let results = await generalApiCall({
      pathname,
      method,
      needsAuthentication: true,
      notShowErrorMessage: true,
      requestSource: source,
    });
    return results;
  };

  const callHighlightApi = async ({ content, source }) => {
    try {
      if (
        !!(getUrlParam('and') || getUrlParam('search') || getUrlParam('or')) ||
        (getUrlParam('topic-id') && getUrlParam('topic-id') !== 'all')
      ) {
        const propertiesHighlight = [
          content.objectId,
          content.objectType,
          queryHighlight,
          ChangeKeywordsExclusions(andKeywords),
          source,
        ];
        return await callHightlightContentAPI(propertiesHighlight);
      }
    } catch (error) {}
  };

  const changeTopicOrQuery = async (props) => {
    const { topic, freeSearch, allActivity } = props ?? {};
    if (topic || freeSearch || allActivity) {
      // Abort previous changeTopicOrQuery if still running
      if (changeTopicOrQueryAbortController.current) {
        changeTopicOrQueryAbortController.current.abort('New topic/query change requested');
      }

      // Create new abort controller for this execution
      const abortController = new AbortController();
      changeTopicOrQueryAbortController.current = abortController;

      // Capture initial params to detect changes during execution
      const initialSearchParam = getUrlParam('search') ?? null;
      const initialTopicParam = getUrlParam('topic-id') ?? null;
      const initialLocationSearch = location.search;

      // Helper function to check if params changed
      const hasParamsChanged = () => {
        const currentSearchParam = getUrlParam('search') ?? null;
        const currentTopicParam = getUrlParam('topic-id') ?? null;
        const currentLocationSearch = location.search;

        return (
          initialSearchParam !== currentSearchParam ||
          initialTopicParam !== currentTopicParam ||
          initialLocationSearch !== currentLocationSearch
        );
      };

      // Helper function to abort if params changed
      const abortIfParamsChanged = () => {
        if (hasParamsChanged() || abortController.signal.aborted) {
          abortController.abort('Params changed during execution');
          // Don't set loading to false here - new call will start, loading should persist
          return true;
        }
        return false;
      };

      if (activityContainer.current) {
        activityContainer.current.scrollToTop();
        activityContainer.current.scrollToLeft();
      }
      setIsLoadingActivity(true);
      if (topic) {
        setLocalTopic(topic);
        setSearchPageQuery('');
      }
      if (freeSearch) {
        setSearchPageQuery(freeSearch);
      }

      // Check if aborted before starting
      if (abortIfParamsChanged()) {
        return;
      }

      const content = Stakeholders.contact;

      const computedLocalTopic = topic ? topic : allActivity ? null : localTopic;
      const computedSearchPageQuery = topic ? '' : freeSearch ? freeSearch : allActivity ? '' : searchPageQuery;
      if (allActivity) {
        setSearchPageQuery('');
      }
      const activeSearchResultsConditionalForRequest =
        ((computedLocalTopic !== null && computedLocalTopic?.id !== null) || computedSearchPageQuery !== '') &&
        stakeholderSearchCondition;

      // Compute matchInformation early so the positions section renders before streamed activity content.
      let ranking;
      let matchInformation;
      if (!!topic && topic?.id !== null) {
        ranking = await callAgregatedSentiment({
          stakeholderId: content?.id,
          keywordListId: topic?.id,
          source: abortController,
        });

        // Check if aborted or params changed after ranking call
        if (abortIfParamsChanged()) {
          return;
        }

        matchInformation = transformMatchingInformationCall({ ranking });
        setContentResults((prev) => ({
          ...prev,
          Stakeholders: {
            ...(prev?.Stakeholders ?? {}),
            ...Stakeholders,
            contact: {
              ...(prev?.Stakeholders?.contact ?? {}),
              ...content,
              matchInformation: matchInformation ?? {},
              ranking,
            },
          },
        }));
      }

      const contentRelated = await relatedContentCall({
        content,
        topic,
        freeSearch: queryToSearch({ search: freeSearch }),
        sourceProp: abortController,
        activeSearchResultsConditional: activeSearchResultsConditionalForRequest,
      });

      // Check if aborted or params changed after relatedContent call
      if (abortIfParamsChanged()) {
        return;
      }

      let profileContentHightlighted = await callHighlightApi({ content, source: abortController });

      // Check if aborted or params changed after highlight call
      if (abortIfParamsChanged()) {
        return;
      }

      //AE: This API is used when switching between clients, so it's important to update groups where the stakeholder is added
      const groups =
        isAuthenticated && !parliamentarianUsers(code) && newAgencyPlan
          ? await listingGroupsForStakeholderOrContact({ stakeholderContactId: content?.id, source: abortController })
          : [];

      // Check if aborted or params changed after groups call
      if (abortIfParamsChanged()) {
        return;
      }

      setContentResults({
        ...contentResults,
        Stakeholders: {
          ...Stakeholders,
          contact: {
            ...content,
            profile:
              !searchIncludesTitleConditional(content.officalName) && profileContentHightlighted?.highlightedHtmlBody
                ? profileContentHightlighted?.highlightedHtmlBody
                : content.profile,
            matchInformation: matchInformation ?? {},
            ranking,
            groups,
          },
          contentRelated: contentRelated,
        },
      });
      navigate(
        `${location.pathname}${topic ? `?topic-id=${topic.id === null ? 'all' : topic.id}` : ''}${
          freeSearch ? `${topic ? '&' : '?'}search=${encodeURIComponent(freeSearch)}` : ''
        }`,
        { replace: true }
      );
      setIsLoadingActivity(false);
      if (topic) {
        collectCall({ contact: Stakeholders.contact, topic });
      }
    }
  };

  const collectCall = async ({ contact, topic }) => {
    const { objectId, objectType, name } = contact;
    const contentUrl = `/stakeholders/${id}/${normalize(name)}`;
    await callCollectContent({ objectId, objectType, topic, contentUrl });
  };

  const callEmployersForStaff = async ({ employers, source }) => {
    try {
      if (!!employers) {
        const employersInformation = [];
        for (let i = 0; i < employers?.length; i++) {
          const employer = employers[i];
          const result = await generalApiCall({
            pathname: `/api/stakeholder/${employer?.id}`,
            method: 'get',
            needsAuthentication: isAuthenticated,
            requestSource: source,
            notShowErrorMessage: true,
          });
          if (!!result) {
            employersInformation.push(result);
          }
        }
        return employersInformation;
      }
    } catch (error) {}
  };

  const load_more_results = async () => {
    if (contentType !== undefined) {
      const { contentTypeToUse, typeToApply, filtersProps, additionalFilters, typeToUse } = contentTypeFilters();
      const { thereIsMoreContent, pageNumber } = contentRelated[contentTypeToUse] ?? {};
      if (thereIsMoreContent) {
        const contentRelatedFromType = await callRelatedContent({
          type: typeToApply,
          content: Stakeholders.contact,
          pageNumber: pageNumber + 1,
          searchWithinQuery: searchPageQuery,
          topic: localTopic,
          filtersProps,
          additionalFilters,
          typeToUse,
        });

        setContentResults({
          ...contentResults,
          Stakeholders: {
            ...Stakeholders,
            contentRelated: { [contentTypeToUse]: contentRelatedFromType },
          },
        });
      }
    }
  };

  const seeAllActivity = () => {
    //AE: It's necessary to get the mastertopic here for those cases when the user only has 1 topic.
    navigate({ search: '' }, { replace: true });
  };

  const { sendEmailToContacts } = useSendEmailToContacts();
  const sendEmailToContact = async ({ e }) => {
    const contactId = Stakeholders?.contact?.contactInformation?.[0]?.id;
    await sendEmailToContacts({
      contactsToSendIds: contactId ? [contactId] : [],
      query: new URLSearchParams(location.search),
      requestSource: source,
      filters: [],
      setIsLoading: () => {},
      focusToField: true,
    });
  };

  const updateChampionStatus = ({ value }) => {
    if (Stakeholders?.contact) {
      let newContactInformation = [...Stakeholders?.contact?.contactInformation];
      newContactInformation[0] = {
        ...newContactInformation[0],
        championStatus: value,
      };
      setContentResults({
        ...contentResults,
        Stakeholders: {
          ...Stakeholders,
          contact: {
            ...Stakeholders?.contact,
            contactInformation: newContactInformation,
          },
        },
      });
    }
  };

  return (
    <Stakeholder
      isBioLoading={isBioLoading}
      isInitialActivityLoading={isInitialActivityLoading}
      StakeholderInformation={Stakeholders?.contact ?? {}}
      contentRelated={Stakeholders?.contentRelated ?? {}}
      activePlan={activePlan}
      enableStakeholders={enableStakeholders}
      stakeholderSearchCondition={stakeholderSearchCondition}
      activeSearch={activeSearch}
      list={list}
      contentType={contentType}
      isLoadingActivity={isLoadingActivity}
      changeTopicOrQuery={changeTopicOrQuery}
      localTopic={localTopic}
      setLocalTopic={setLocalTopic}
      searchPageQuery={searchPageQuery}
      keywordsLists={keywordsLists}
      load_more_results={load_more_results}
      seeAllActivity={seeAllActivity}
      activityContainer={activityContainer}
      clickOnSeeMore={clickOnSeeMore}
      setClickOnSeeMore={setClickOnSeeMore}
      sendEmailToContact={sendEmailToContact}
      updateChampionStatus={updateChampionStatus}
    />
  );
};

export default StakeholderPage;
